/**
 * Horarios de apertura por día de la semana.
 *
 * El campo `schedule` de una categoría es un objeto cuyas llaves son el nombre del día en inglés
 * y minúscula, y cuyos valores son las horas (0~23) en que esa categoría está abierta. Una hora
 * presente en el arreglo significa que la categoría está abierta durante toda esa hora, así que
 * una tienda que abre los lunes de 9 a 11 se escribe { monday: [9, 10] }.
 *
 * Una categoría sin `schedule` (o con uno vacío o mal formado) no tiene restricción horaria y se
 * muestra siempre, igual que antes de que existiera este campo.
 */

export const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const isHour = (value) => Number.isInteger(value) && value >= 0 && value <= 23;

/**
 * Normaliza el `schedule` que viene de Firestore.
 *
 * @param {unknown} value - Valor crudo del documento.
 * @returns {Object|null} Un objeto con las 7 llaves y sus horas ordenadas y sin repetir, o null
 *   si el valor no es un horario utilizable (no es objeto, o no deja ninguna hora abierta).
 */
export function toSchedule(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const schedule = {};
  let openHours = 0;

  WEEKDAY_KEYS.forEach((key) => {
    const hours = Array.isArray(value[key]) ? value[key].filter(isHour) : [];
    schedule[key] = [...new Set(hours)].sort((a, b) => a - b);
    openHours += schedule[key].length;
  });

  return openHours > 0 ? schedule : null;
}

/**
 * Indica si un horario ya normalizado está abierto en un momento dado.
 *
 * @param {Object} schedule - Horario retornado por toSchedule.
 * @param {Date} now - Momento a evaluar.
 * @returns {boolean}
 */
export function isScheduleOpen(schedule, now) {
  const hours = schedule?.[WEEKDAY_KEYS[now.getDay()]] || [];
  return hours.includes(now.getHours());
}

/**
 * Indica si una categoría se puede mostrar según su horario.
 *
 * @param {Object} category - Categoría tal como viene de Firestore.
 * @param {Date} now - Momento a evaluar (por defecto, ahora).
 * @returns {boolean} true si la categoría no tiene horario o si está dentro de él.
 */
export function isCategoryOpen(category, now = new Date()) {
  const schedule = toSchedule(category?.schedule);
  if (!schedule) return true;
  return isScheduleOpen(schedule, now);
}
